import type { FC, ReactNode } from 'react';

// Externals
import PropTypes from 'prop-types';
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';

// Store
import { useSelector } from '../store';
import { Shop } from 'src/types/shop.model';

interface AuthGuardProps {
  children: ReactNode;
}

const AuthGuard: FC<AuthGuardProps> = ({ children }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { shopId } = useParams<{ shopId: string }>();

  const isAuthenticated = useSelector<boolean>((state) => state.authentication.isAuthenticated);
  const isAdmin = useSelector<boolean>((state) => state.authentication.isAdmin);
  const shops = useSelector<Shop[]>((state) => state.authentication.shops);

  if (!isAuthenticated) {
    return <Navigate replace={true} to={`/login?redirect=${encodeURIComponent(location.pathname)}`} />;
  }

  if (isAdmin) {
    return <Navigate replace={true} to={'/admin'} />;
  }

  if (shopId && !shops.find((shop) => shop.id === +shopId)) {
    return <Navigate replace={true} to={'/404'} />;
  }

  // This is done so that in case the route changes by any chance through other
  // means between the moment of request and the render we navigate to the initially
  // requested route.
  if (searchParams.get('redirect') && location.pathname !== searchParams.get('redirect')) {
    return <Navigate replace={true} to={searchParams.get('redirect')} />;
  }

  return <>{children}</>;
};

AuthGuard.propTypes = {
  children: PropTypes.node
};

export default AuthGuard;
